import { createFeatureSelector, createSelector } from '@ngrx/store';
import { complianceFeatureKey } from 'src/app/compliance/store/selectors';
import { ComplianceState } from 'src/app/compliance/store/state/compliance.state';
import { selectQueryParam, selectRouteParam } from 'src/app/core/router.selectors';
import { complianceFeature } from 'src/app/compliance/store/selectors/shipping-requests.selector';

export const complianceState = createFeatureSelector<ComplianceState>(complianceFeatureKey);

export const selectCompliancePermissions = createSelector(
    complianceState,
    (state) => state?.compliancePermissions?.compliancePermissions
);

export const selectComplianceDocumentDetails = createSelector(complianceState, (state) => state?.documentReviewer);

export const selectComplianceReportPermissions = createSelector(complianceState, (state) => {
    return state.complianceInfo?.permissions;
});

export const selectCredentialingSpecialists = createSelector(
    complianceState,
    (state) => state.complianceInfo?.credentialingSpecialists
);

export const selectComplianceLiaisons = createSelector(
    complianceState,
    (state) => state.complianceInfo?.complianceLiaisons
);

export const selectFilteredCredentialingSpecialists = createSelector(
    complianceState,
    (state) => state.complianceInfo?.filteredCredentialingSpecialists
);

export const selectLoadingFlag = createSelector(complianceState, (state) => state.complianceInfo?.isLoading);

export const selectCandidateInformation = createSelector(
    complianceState,
    (state) => state.complianceInfo?.candidateInfo
);

export const selectComplianceContracts = createSelector(complianceState, (state) => state.contracts.contracts);

export const selectVaccineManufacturers = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.vaccineManufacturers
);

export const selectSubmitBtnClickedStatus = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.submitBtnClicked
);

export const selectComplianceCandidateId = createSelector(
    complianceState,
    selectRouteParam('candidateId'),
    (state, candidateId) => Number(candidateId)
);

export const selectComplianceRejecteDocumentStatus = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.rejectedDocumentSuccess ?? false
);

export const selectComplianceRejectedPages = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.details?.rejectedPageCount
);

export const selectComplianceAssignedPages = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.details?.assignedPageCount
);

export const selectComplianceRejectedAssignedPages = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        assigned: state.details?.assignedPageCount,
        rejected: state.details?.rejectedPageCount
    };
});

export const selectIsDocumentMovedToWasteStatus = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        isDocumentMovedToWasteStatus: state.details?.isDocumentMovedToWasteStatus ?? false
    };
});

export const selectIsEsignatureAutoPost = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        isEsignatureAutoPost: state.details?.isEsignatureAutoPost ?? false
    };
});

export const selectIsExternalUpload = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        isExternalUpload: state.details?.isExternalUpload ?? false
    };
});

export const selectComplianceAssignedUploaderId = createSelector(
    complianceState,
    selectQueryParam('assignedUploaderId'),
    (state, uploaderId) => (isNaN(Number(uploaderId)) ? 0 : Number(uploaderId))
);

export const selectComplianceIsSignedContract = createSelector(
    complianceState,
    selectQueryParam('isSignedContract'),
    (state, isSignedContract) => (isSignedContract === 'true' ? true : false)
);

export const selectComplianceIsCandidateUpload = createSelector(
    complianceState,
    selectQueryParam('isCandidateUpload'),
    (state, isCandidateUpload) => (isCandidateUpload === 'true' ? true : false)
);

export const selectComplianceRouteArgs = createSelector(
    complianceState,
    selectComplianceCandidateId,
    selectComplianceAssignedUploaderId,
    selectComplianceIsSignedContract,
    selectComplianceIsCandidateUpload,
    (state, candidateId, uploaderId, signedContract, candidateUpload) => {
        return {
            candidateId: candidateId,
            uploaderId: uploaderId,
            signedContract: signedContract,
            candidateUpload: candidateUpload
        };
    }
);

export const selectComplianceDocumentReviewerDetails = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.details
);

export const selectComplianceDocumentReviewerDocumentIds = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.documentIds
);

export const selectComplianceSelectedDocumentId = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.selectedDocumentId
);

export const selectComplianceCandidateDocumentId = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.candidateDocument?.documentId ?? 0
);

export const selectComplianceCandidateDocument = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.candidateDocument
);

export const selectComplianceSelectedDocument = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        candidateDocument: state?.candidateDocument,
        document: state?.document
    };
});

export const selectComplianceContractSelected = createSelector(selectComplianceDocumentDetails, (state) => {
    return state?.selectedContract;
});

export const selectedCandidateLiaisonName = createSelector(selectComplianceDocumentDetails, (state) => {
    return state?.candidateLiaisonName ?? 'No Available Name';
});

export const selectComplianceDocumentTypes = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.documentTypes
);

export const selectComplianceDocumentReviewerPagesRendered = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.documentPages
);

export const selectComplianceDocumentReviewerAssignmentResult = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.assignDocumentResult
);

export const selectComplianceDocumentReviewerMergeChoice = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.mergeChoice
);

export const selectComplianceDocumentReviewerTextRequirements = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.textRequirements
);

export const selectComplianceDocumentReviewerRequirementDescriptions = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.textRequirementDescriptions
);

export const selectComplianceDocumentReviewerSelectedDocumentType = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.selectedDocumentType
);

export const selectDocumentReviewerValidSelections = createSelector(selectComplianceDocumentDetails, (state) => {
    const selectedDocType = state?.selectedDocumentType;
    const selectedContract = state?.selectedContract;
    const validForm = state?.isValidForm;

    return selectedContract && selectedDocType && validForm;
});

export const selectComplianceDocumentReviewerRejectConfirmState = createSelector(
    selectComplianceDocumentDetails,
    (state) => {
        return {
            readonly: state?.readonlyView,
            mergeChoice: state?.mergeChoice
        };
    }
);

export const selectComplianceDocumentReviewerRejectedDocumentArgs = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.rejectedDocumentArgs
);

export const selectComplianceDocumentReviewerAssignedDocumentState = createSelector(
    selectComplianceDocumentDetails,
    (state) => {
        return {
            candidateDocumentId: state?.candidateDocument?.id ?? 0,
            pages: state?.documentPages ?? [],
            contractId: state?.selectedContract?.id ?? null,
            documentTypeId: state?.selectedDocumentType?.id ?? 0,
            resolution: state?.mergeChoice ?? null,
            candidateVaccine: state?.candidateVaccine ?? undefined,
            candidateDocumentHistory: state?.selectedDocumentHistory ?? undefined
        };
    }
);

export const selectDocumentReviewerSteps = createSelector(selectComplianceDocumentDetails, (state) => {
    return {
        current: state?.currentReviewerStep,
        previous: state?.previousReviewerStep
    };
});

export const selectComplianceReassignInLegacy = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.reAssignedPagesinLegacy
);

export const getAllPagesSelectedStatus = createSelector(
    selectComplianceDocumentDetails,
    (state) => state?.isAllPagesSelected
);

export const selectFacilityContractAssignment = createSelector(complianceState, (state) => {
    return state.complianceInfo?.facilityContractAssignment;
});

export const selectLiaisonFacilityContractAssignment = createSelector(
    complianceState,
    (state) => state.complianceInfo?.liaisonFacilityContractAssignment
);

export const selectLiaisonQmAssociationAssignment = createSelector(
    complianceState,
    (state) => state.complianceInfo?.liaisonQmContractAssignment
);

export const selectConfirmBtnClickedStatus = createSelector(
    selectComplianceDocumentDetails,
    (state) => state.confirmBtnClicked
);

export const selectComplianceRequestsAssignees = createSelector(
    complianceState,
    (state: ComplianceState) => state.complianceRequests.assignees
);

export const isCurrentUserInComplianceRequestsAssignees = (name: string) =>
    createSelector(
        complianceFeature,
        (state) =>
            state.complianceRequests.assignees &&
            state.complianceRequests.assignees.length > 0 &&
            state.complianceRequests.assignees.filter((a) =>
                a.name.toLowerCase().trim().endsWith(name.toLowerCase().trim())
            ).length > 0
    );

export const selectScheduleOrientation = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.scheduleOrientation
);

export const selectScheduleOrientationSessions = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.scheduleOrientationSessions
);

export const selectActionRequiredOrientationSessionsCount = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.actionRequiredOrientationSessionsCount
);

export const selectScheduleOrientationSessionsNeedsRefresh = createSelector(complianceState, (state) => {
    const data = state?.scheduleOrientation?.selectedScheduleOrientation;
    const needRefresh = state?.scheduleOrientation?.sessionsNeedsRefresh;
    return {
        selectedOrientation: data,
        sessionsNeedsRefresh: needRefresh ?? true
    };
});

export const selectScheduleOrientationStatuses = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.scheduleOrientationStatuses
);

export const selectScheduleOrientationSessionTypes = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.scheduleOrientationSessionTypes
);

export const selectScheduleOrientationPermission = createSelector(
    complianceState,
    (state: ComplianceState) => state?.scheduleOrientationPermission?.orientationPermissionId
);

export const selectLoadingScheduleOrientationFlag = createSelector(
    complianceState,
    (state) => state.scheduleOrientation?.loading
);

export const selectSessionsNextDay = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.sessionsNextDay
);

export const selectDoesAnyOrientationExist = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.doesAnyOrientationExist
);

export const selectMultiBundlesStatuses = createSelector(
    complianceState,
    (state: ComplianceState) => state.scheduleOrientation?.multiBundlesStatuses
);
